/**
 * Módulo que gerencia o estado das mensagens exibidas na aplicacao
 * @state Estados que devem ser controlados
 * @mutations Funções que tem permissão para alterar o state
 * @actions Executar operações asincronas e chamar mutations
 * @getters Retorna o estado além da possibilidade de retornar a
 * mesma lógica do computed (Gera valores com base em valores originais)
 */

const state = {
	aboutToRelease: [],
	releases: [
		{
			// Julho de 2026
			timestamp: 1783944000000, // 2026-07-13 12:00 UTC
			version: '',
			changes: {
				added: [
					'addedPrognosisAsyncProcessing',
					'addedBindPrognosisRichFilters',
					'addedPlantingAgeRangeFilter',
					'addedPrognosisChartImagePrefetch',
					'addedCustomDashboardInApp',
					'addedCsvExportEscaping',
				],
				fixed: [
					'fixedBindCsvFarmMatching',
					'fixedMultiselectSearchError',
					'fixedPlantingExportDecimalComma',
				],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '2.1.0',
				},
			],
		},
		{
			// Junho de 2026
			timestamp: 1782820800000, // 2026-06-30 12:00 UTC
			version: '',
			changes: {
				added: [
					'addedSiteClassificationByFilter',
					'addedDashboardSelectAllStrata',
					'addedAlertConfigDeletionConfirmation',
				],
				fixed: [
					'fixedPrognosisMemoryLimits',
					'fixedPrognosisPayloadLimit',
					'fixedBasalAreaCoefficientsOrder',
					'fixedProfilePhotoExpiration',
					'fixedInventoryExportAndStratumLoading',
				],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '2.0.3',
				},
			],
		},
		{
			// Maio de 2026
			timestamp: 1780228800000, // 2026-05-31 12:00 UTC
			version: '',
			changes: {
				added: [
					'addedServiceOrderOperationStep',
					'addedDendrometerVersionSelection',
					'addedDashboardKpiTooltips',
				],
				changed: ['changedClutterBasalAreaParams'],
				fixed: [
					'fixedDashboardEmptyInventory',
					'fixedDashboardStrataReset',
					'fixedDashboardPlantingAge',
					'fixedFireRiskDashboard',
					'fixedMeasurementImportValidation',
					'fixedTreeviaAcademyReload',
					'fixedAlertConfigFarmDropdown',
				],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '2.0.2',
				},
			],
		},
		{
			// Abril de 2026
			timestamp: 1777550400000, // 2026-04-30 12:00 UTC
			version: '',
			changes: {
				changed: ['changedDesktopMenuNoWrap'],
				fixed: ['fixedCrudTableActionsColumn'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '2.0.1',
				},
			],
		},
		{
			timestamp: 1776945600000, // 2026-04-23 12:00 UTC
			version: '',
			html: 'changelogFireMonitoringRelease',
			changes: {},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '2.0.0',
				},
			],
		},
		{
			timestamp: 1770654520137, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				added: ['addedForestStratumColumnProcessingGrid', 'addedPaginationBindPrognosisPage'],
				fixed: [
					'fixedPrognosisBindingGridLoadBehavior',
					'fixedPaginationComponentInitialDataLogic',
					'fixedKeywordSearchSpaceRemoval',
					'fixedGridPaginationAfterClearSearch',
				],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.11.6',
				},
			],
		},

		{
			timestamp: 1769299200000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				fixed: [
					'fixedSiteClassificationCodeStartNewAccounts',
					'fixedPrognosisCodeStartNewAccounts',
					'fixedAlgebraicDifferenceValidationSiteModeling',
					'fixedPrognosisSearchWithoutFarm',
					'fixedSearchAfterClearFilter',
					'fixedSiteModelParameterQuery',
				],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.11.4',
				},
			],
		},

		{
			timestamp: 1766188800000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				added: ['addedNullValueHandlingProcessingKPIs', 'addedSamplesColumnServiceOrder'],
				fixed: ['fixedScatterChartScaleStartAtZero', 'fixedAdjustedModelsEndpointNullValues'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.11.3',
				},
			],
		},

		{
			timestamp: 1763704800000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				added: ['addedStratumViewDashboardAnalytics', 'addedApiKeysIntegrationModule'],
				changed: ['changedDashboardKPIConversionRules', 'removedPlantingIdFilterOSEdit'],
				fixed: [
					'fixedServiceOrderGridEmptyList',
					'fixedStratumExportFilename',
					'fixedWelcomeEmailNewUser',
				],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.11.2',
				},
			],
		},

		{
			timestamp: 1761717600000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				added: [
					'addedTreeEditOnBoxplot',
					'addedPowerBIDashboard',
					'addedRequestSizeLimitPrognosis',
					'addedPlotCoordinatesAndDateQualityExport',
				],
				changed: ['fixedSubsequentVariableLogic'],
				fixed: [
					'fixedPhytosanitaryFiltersAndCharts',
					'addedRectangularPlotFormat',
					'fixedUserEditForm',
					'fixedCompiledInventoryExportCalculations',
					'fixedServiceOrderPlanningCubingOverwrite',
				],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.11.1',
				},
			],
		},

		{
			timestamp: 1757080927390, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				added: ['addedConsistencyAndValidation'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.11.0',
				},
			],
		},
		{
			timestamp: 1753460038859, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				added: ['treeviaAcademyFeature'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.10.9',
				},
			],
		},
		{
			timestamp: 1748526855000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				added: [
					'adddedStrictCubing',
					'addXButtonStrataCards',
					'confirmDeleteSelectedItems',
					'improvedSearchScreenEfficiency',
					'resetClimateDataMultipleFarms',
					'autoUpdateClimateDataAnalyticsDashboard',
					'mapObservationColumnMeasurementImport',
				],
				changed: ['userCanSearchBySODescriptionInProcessing', 'redesignAppConfigPage'],
				fixed: [
					'apiRoutePlotDeletion0',
					'calculationAgeGeneticMaterialScalingExport',
					'calculationUsableStemsCompiledInventory',
					'dashboardSummaryTableNcovas',
					'strataPlanningDisableLinkedPlots',
					'measurementExportCoordinatesCommaDecimal',
					'farmHistoryChartNullValues',
					'outlierExportIncludeCollectionDate',
					'areaConversionSapIntegration',
					'adjustMappingCustomVariable',
					'fixedTranslationModule',
				],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.10.8',
				},
			],
		},
		{
			timestamp: 1743692240289, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				added: ['addedButtonViewPassword'],
				fixed: ['fixedModelAccess'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.10.7',
				},
			],
		},

		{
			timestamp: 1743440182418, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				changed: [
					'preventUserToAccessQualityDataIfNotExists',
					'addedRiskLevelToTooltip',
					'addedQualityDataToExportCSV',
				],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.10.6',
				},
			],
		},
		{
			timestamp: 1741882993635, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				added: ['addedPlantingDate'],
				fixed: [
					'errorEditingOutlier',
					'inconsistentDataExport',
					'requestCronogramaOrdemServico',
					'buttonExportInventory',
				],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.10.5',
				},
			],
		},
		{
			timestamp: 1741630219066, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				added: ['addedPaginationToPages'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.10.4',
				},
			],
		},
		{
			timestamp: 1741094763772, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				added: ['addedTreetopField', 'addedQualityFieldsFixingAbility'],
				fixed: ['fixBoxplotVisualization'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.10.3',
				},
			],
		},
		{
			timestamp: 1740625200000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",

			changes: {
				fixed: ['fixPlantationMappingOnCubingImport'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.10.2',
				},
			],
		},
		{
			timestamp: 1739847600000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",

			changes: {
				fixed: ['returnToInitPageOnSTProcessFinish'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.10.1',
				},
			],
		},
		{
			timestamp: 1739242800000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",

			changes: {
				added: ['introducingQualityInventory', 'addedBrandNewPlotExportPage', 'addedIASelectModel'],
				changed: ['enchancedSamplingCasualCalc', 'addedEstimatedTreesToHipsometricChart'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.10.0',
				},
			],
		},
		{
			timestamp: 1737514800000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				fixed: ['fixedMessageFormatOnImportingPlot'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.9.1',
				},
			],
		},
		{
			timestamp: 1734577200000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				changed: ['reducedMinimumSamplesByPlantation'],
				added: ['introducingActivityFlag', 'introducingProcessFlag'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.9.0',
				},
			],
		},
		{
			timestamp: 1734318000000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				fixed: ['addedOpenClosePlotOptionToOS'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.8.18',
				},
			],
		},
		{
			timestamp: 1733972400000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				fixed: ['addedCustomDashboardModuleValidation'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.8.17',
				},
			],
		},
		{
			timestamp: 1733454000000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				fixed: ['addedPowerBIDashboardView'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.8.16',
				},
			],
		},
		{
			timestamp: 1731898800000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				fixed: ['fixedSampleCodeOnOSSaving'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.8.15',
				},
			],
		},
		{
			timestamp: 1731553200000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				fixed: ['fixedFarmDropdownMapping'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.8.14',
				},
			],
		},
		{
			timestamp: 1731294000000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				changed: ['changedRulesToPlotFarmCharts'],
				fixed: ['fixedUploadMeasurementsFlow', 'fixedButtonDisabledLogic'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.8.13',
				},
			],
		},
		{
			timestamp: 1730343600000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				changed: ['adjustmentsToCubingProcess'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.8.12',
				},
			],
		},
		{
			timestamp: 1730257200000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				added: [
					'addedButtonToSaveSiteClassification',
					'addedEditonToOSTimeline',
					'addedMethodToSavePrognosis',
					'addedLoadingToInitialTableUpdates',
				],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.8.11',
				},
			],
		},
		{
			timestamp: 1729825200000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				added: ['addedButtonToSaveProcessing'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.8.10',
				},
			],
		},
		{
			timestamp: 1727233200000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				added: ['stackMeasurementExportButton'],
				removed: ['removedExportStackMeasurementButton'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.8.9',
				},
			],
		},
		{
			timestamp: 1726801200000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				added: ['addedCorrectMappingInterestVariable'],
				fixed: ['maxBHDMapping'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.8.8',
				},
			],
		},
		{
			timestamp: 1726714800000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				added: ['exportStackInventoryData'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.8.7',
				},
			],
		},
		{
			timestamp: 1724814000000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				added: ['addedFilterToRemoveBlankLinesFromCSV'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.8.6',
				},
			],
		},
		{
			timestamp: 1723604400000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				fixed: ['fixedSelectedSamplesDiametricClasses'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.8.5',
				},
			],
		},
		{
			timestamp: 1723172400000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				fixed: ['fixedPlotSizeFieldFormat'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.8.4',
				},
			],
		},
		{
			timestamp: 1723086000000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				fixed: ['fixedOSSingleTreesFlow'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.8.3',
				},
			],
		},
		{
			timestamp: 1722999600000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				changed: ['changedPositionInventoryExport'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.8.2',
				},
			],
		},
		{
			timestamp: 1722913200000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				changed: ['plotToAcceptDecimal'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.8.1',
				},
			],
		},
		{
			timestamp: 1722602714200, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				fixed: [
					'clFormatoDataPlantioEDataColeta',
					'clSalvarModificacoesOS',
					'clDadosClimaticos',
					'clDadosParaPrognose',
					'clRemedicaoParcelas',
					'clInstrucaoMedicao',
					'clRangeDataClassificacaoDeSitioPrognose',
					'clCadastroDeUsuarios',
					'clCadastroPoligonoFazenda',
					'clCadastroDePlantioSemMaterialGenetico',
					'clTipoDoEstrato',
					'clPlanejamentoDeCubagem',
				],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.8.0',
				},
			],
		},

		{
			timestamp: 1717417440819, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				added: ['includedExportSummaryTable', 'addedExportBetas'],
				fixed: [
					'moveSummaryTable',
					'includedScrollDropbox',
					'giveAppropriateFeedback',
					'correctedLabelVtcc',
					'fixedFailureAssortmentData',
					'fixedCoropleticMap',
				],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.7.0',
				},
			],
		},

		{
			timestamp: 1713890711905, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				added: [
					'importHistoricalInvetory',
					'exportMoreThanOneInventoryProccess',
					'paginationOfListWorkOrders',
				],
				fixed: ['informacionFechaInicio', 'unitOfWorkColumns'],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.6.0',
				},
			],
		},

		{
			timestamp: 1712336184000, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				added: [
					'changelogOsPilha',
					'changelogProcessamentoPilha',
					'changelogExportacaoDadoProcessadoPilha',
				],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.5.3',
				},
			],
		},

		{
			timestamp: 1711541529987, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				fixed: [
					'fixedGeofenceInput',
					'fixedPhoneInput',
					'fixedTamanhoPrla',
					'fixedOptionalOSfields',
					'fixedTitleRegisterOS',
					'fixedFieldsCoeficientBehavior',
					'fixedEditingBehaviorOS',
					'fixedMeasureImportCsv',
					'fixedUploadParcelOS',
					'fixedWOtimeline',
				],
				// removed: [],
				added: ['woCSVexport', 'searchDropdownStratumPlanning'],
				// changed: [],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.5.2',
				},
			],
		},

		{
			timestamp: 1710446680675, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				fixed: [
					'fixedEditProduct',
					'fixedEditMeasurement',
					'fixedEditFarm',
					'behaviorShapeInportFields',
					'allFieldsMandatory',
					'whenImportingMeasurement',
					'whenSavingMeasurement',
					'fixedBehaviorFarmRegistration',
					'improvedDashboardAnalitycs',
					'changedOptimizationOS',
					'includedMask',
				],
				// removed: [],
				added: ['buttonEportDataRgistration', 'userNameDisplay'],
				// changed: [],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.5.1',
				},
			],
		},

		{
			timestamp: 1705944236732, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				// changed: ["addOptionIfGenMatIsAlreadyRegistred"],
				// removed: [],
				added: ['serviceOrderCronogram', 'menuModules'],
				// changed: [],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.5.0',
				},
			],
		},

		{
			timestamp: 1702638378288, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				// changed: ["addOptionIfGenMatIsAlreadyRegistred"],
				// removed: [],
				added: [
					'nonMandatoryAlertsWithotherSymbols',
					'duplicateTreeCategoryRegistration',
					'DataMaskMeasurementRegistration',
					'stratumTypeRegistrationList',
				],
				fixed: [
					'saveButtonExportOutliersarenotworking',
					'chartDownloadButtonsonDashboardsdontwork',
					'correctioninPrognosisAdjustment',
					'CubageRegistrationModalOpen',
					'ViewScatterScreenLoadData',
					'FailedImportPlotsSingleTreesOS',
					'EditButtonSiteClassificationPrognosis',
				],
				// changed: [],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.4.1',
				},
			],
		},

		{
			timestamp: 1697830721427, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				// changed: ["addOptionIfGenMatIsAlreadyRegistred"],
				// removed: [],
				added: ['dashboardUpdatesStratumLevel', 'addedMoreInfoToStratumExport'],
				fixed: ['fixedStratumPlanningFilterCustomVariables'],
				// changed: [],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.4.0',
				},
			],
		},
		{
			timestamp: 1697046489822, //new Date().getTime() no console do chrome
			version: '', //3.10.2",
			changes: {
				// changed: ["addOptionIfGenMatIsAlreadyRegistred"],
				// removed: [],
				changed: [
					'exactSampleIntensityOnGeneratingPlots',
					'measureUnitsAdjustedForExport',
					'adjustOutlierClassification',
				],
				added: ['addeduserProfilePicture'],
				fixed: ['bindPlotAreaOS', 'deleteOutlierByClick'],
				// changed: [],
				removed: ['samplesIntensityAlertRemovedOnPlotDragout'],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.3.0',
				},
			],
		},
		{
			timestamp: 1696601902441, //new Date().getTime() no console do chrome
			version: '', //3.10.1",
			changes: {
				// changed: ["addOptionIfGenMatIsAlreadyRegistred"],
				// removed: [],
				// changed: ["improvedPrognosisFlow"],
				added: ['outlierChartSelection', 'colorToTreeCategoryRegistry'],
				// fixed: ["minorBugFixes"],
				// changed: [],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.2.1',
				},
			],
		},
		{
			timestamp: 1696016055266, //new Date().getTime() no console do chrome
			version: '', //3.09.3",
			changes: {
				changed: ['addOptionIfGenMatIsAlreadyRegistred'],
				// removed: [],
				// changed: ["improvedPrognosisFlow"],
				added: ['addStratumExport', 'addChoroplethDashboardMap', 'addAccessControlToModules'],
				fixed: ['minorBugFixes'],
				// changed: [],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.2.0',
				},
			],
		},
		{
			timestamp: 1695408194131, //new Date().getTime() no console do chrome
			version: '', //3.09.2",
			changes: {
				changed: [
					'adjustTextsOnAlerts',
					'addedMoreInfoToPlotExport',
					'changedLabelOfCubbingImport',
					'adequateStratumCreationForm',
					'chageTheWayPlotAreColored',
					'chagedSomeUIElementsAndPositions',
					'addTabsToSapareteUploadPlots',
					'adequateShapeColors',
				],
				removed: ['removedSampleIntensiyAlert'],
				// changed: ["improvedPrognosisFlow"],
				added: [
					'addedAlertOnMeasurementImportFinishing',
					'addedUnitToExports',
					'addedOptionToDownloadOutliersInMeasurementFlow',
					'addedEditProfileShortcut',
					'addedUnitOnCubbingInstruction',
				],
				fixed: [
					'someBugsImportMeasurementFlow',
					'adjustedStratumTypeBinding',
					'fixedCollectorPasswordValidator',
					'fixedSameProductDescriptionAlert',
					'fixedPlotAreaBindingOnOs',
					'fixedPrognosisFetchForData',
					'fixedSomeBugsOnTreeCategory',
					'fixedBugOnChangingPassword',
					'fixedSearchBarRegistryTable',
					'fixedDownloadCorrectShape',
					'fixedOutlierExclusion',
					'fixedNotThrowingPlotOnEdge',
					'fixedClassificationOfOutliersOnVisulization',
					'fixedCustomVariablesToFilterPlantationOnStratification',
					'fixedAutocompleteImportShapes',
					'fixedUploadShapesOnAFarmRegistry',
				],
				// changed: [],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.1.1',
				},
			],
		},
		{
			timestamp: 1693611716575, //new Date().getTime() no console do chrome
			version: '', //3.09.1",
			changes: {
				// changed: ["improvedPrognosisFlow"],
				added: ['addedPopulationLevelHistory', 'addedAssortmentVisualization'],
				// changed: [],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.1.0',
				},
			],
		},
		{
			timestamp: 1692382029913, //new Date().getTime() no console do chrome
			version: '', //3.08.2",
			changes: {
				changed: ['improvedPrognosisFlow'],
				added: ['addedRecoveryPassFlow', 'introducingIntegration'],
				// changed: [],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.0.1',
				},
			],
		},
		{
			timestamp: 1691171036372, //new Date().getTime() no console do chrome
			version: '', //3.08.1",
			changes: {
				changed: [
					'improveUploadMeasurementFlow',
					'adjustPlotGenerationCalculation',
					'adjustConsistencyOfOSFlow',
				],
				fixed: ['variousBugFixes'],
				// changed: [],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '1.0.0',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1688388525635, //new Date().getTime() no console do chrome
			sprint: '200',
			description: '',
			version: '', //3.07.1",
			changes: {
				changed: ['removeTreeWarnings', 'fixShapeCleaning'],
				added: ['addedModelNameToTable', 'enableSingleTreesExport', 'enableOSReport'],
				// changed: [],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.17.6-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1687540400763, //new Date().getTime() no console do chrome
			sprint: '200',
			description: '',
			version: '', //3.06.3",
			changes: {
				changed: ['fixedChartPosition', 'makeDownloadedFileBetter', 'allowAlphaNumericValueToTree'],
				added: ['addedAutomaticCard', 'improveprocessingFormExperience'],
				deprecated: ['removeNewMeasurement'],

				// changed: [],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.17.5-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1687438416497, //new Date().getTime() no console do chrome
			sprint: '200',
			description: '',
			version: '', //3.06.2",
			changes: {
				changed: ['uppercaseCategoryName'],
				added: [
					'addedCategoryColor',
					'addedDiameterVerification',
					'addedUnityAndAdjustLabels',
					'addedInformativeTextsToCubbingPlan',
				],
				deprecated: ['removedPlotCreation'],

				// changed: [],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.17.4-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1687178299851, //new Date().getTime() no console do chrome
			sprint: '200',
			description: '',
			version: '', //3.06.1",
			changes: {
				changed: ['fixedPlantatinGeojsonDownload', 'makeCodeToBeUniqueOnError'],
				added: [
					'addedLoadingLoginButton',
					'addedLoadingDeleteAccessProfile',
					'addedCheckIconFarmImport',
					'makeVisibleCustomVariablesOnSectionEditionForm',
					'addedFieldCodeToPlantationEditionForm',
					'addedNStemsFieldToPlantationEditionForm',
					'addedErrorNStemsFieldToPlantationEditonForm',
					'makeVisibleCustomVariablesOnPlantationEditionForm',
				],

				// changed: [],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.17.3-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1685127806727, //new Date().getTime() no console do chrome
			sprint: '200',
			description: '',
			version: '', //3.05.6",
			changes: {
				changed: ['minorInventoryProcessing'],
				// added: [],

				// changed: [],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.17.2-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1685026972412, //new Date().getTime() no console do chrome
			sprint: '200',
			description: '',
			version: '', //3.05.5",
			changes: {
				changed: ['minorCubingprocessing'],
				// added: [],

				// changed: [],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.17.1-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1684428553453, //new Date().getTime() no console do chrome
			sprint: '200',
			description: '',
			version: '', //3.05.4",
			changes: {
				added: ['addedExportPrognosis'],
				changed: ['minorPrognosis'],
				// added: [],

				// changed: [],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.17.0-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1684345074729, //new Date().getTime() no console do chrome
			sprint: '200',
			description: '',
			version: '', //3.05.3",
			changes: {
				changed: ['minorDashboardAnalysisChanges'],
				// added: [],

				// changed: [],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.16.2-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1683726563074, //new Date().getTime() no console do chrome
			sprint: '200',
			description: '',
			version: '', //3.05.2",
			changes: {
				changed: ['minorCubingChanges'],
				// added: [],

				// changed: [],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.16.1-alpha',
				},
			],
		},
		{
			type: 'minor',
			timestamp: 1683125740252, //new Date().getTime() no console do chrome
			sprint: '200',
			description: '',
			version: '', //3.05.1",
			changes: {
				// changed: [

				// ],
				added: ['introducingPrognosis'],

				// changed: [],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.16.0-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1682431675906, //new Date().getTime() no console do chrome
			sprint: '200',
			description: '',
			version: '', //3.04.5",
			changes: {
				// changed: [

				// ],
				added: ['addedTableSorting', 'addedDatePickerMap'],

				changed: ['addedDatePickerInDateSelections'],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.15.3-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1682345646204, //new Date().getTime() no console do chrome
			sprint: '200',
			description: '',
			version: '', //3.04.4",
			changes: {
				// changed: [

				// ],
				added: ['addedStandardDeviationAndVarianceCoeficient', 'addedClearFiltersButton'],

				// changed: [],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.15.2-alpha',
				},
			],
		},
		{
			type: 'minor',
			timestamp: 1682022562334, //new Date().getTime() no console do chrome
			sprint: '200',
			description: '',
			version: '', //3.04.3",
			changes: {
				// changed: [

				// ],
				added: [
					'addedSeriesSortingMeasurementChart',
					'addedChosenVariablePersist',
					'addedAbsoluteViewTemporalChart',
					'addedChangeVariableAbility',
					'addedUnityToChart',
					'addedExportTableAbility',
					'addedSampleViewToMap',
					'addedPlantationCodeAsVariable',
				],

				changed: [
					'changedRankingSeriesInProductivityAnalysis',
					'multipleFarmSelectionIndicators',
					'showProperNameGreeting',
					'changeColorOfChartLines',
					'changeColorsOfHeatmap',
				],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.15.1-alpha',
				},
			],
		},
		{
			type: 'minor',
			timestamp: 1681748395620, //new Date().getTime() no console do chrome
			sprint: '200',
			description: '',
			version: '', //3.04.2",
			changes: {
				// changed: [

				// ],
				added: ['addedDashboardAnalysis'],

				// changed: ["lorem ipsum"],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.15.0-alpha',
				},
			],
		},
		{
			type: 'minor',
			timestamp: 1680812336348, //new Date().getTime() no console do chrome
			sprint: '200',
			description: '',
			version: '', //3.04.1",
			changes: {
				// changed: [

				// ],
				added: ['addUpdateOSPLResource'],

				// changed: ["lorem ipsum"],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.14.0-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1679921860865, //new Date().getTime() no console do chrome
			sprint: '200',
			description: '',
			version: '', //3.03.6",
			changes: {
				changed: ['outliersChartVisualisation'],
				added: ['addedAppConfigPage'],

				// changed: ["lorem ipsum"],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.13.6-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1679588925683, //new Date().getTime() no console do chrome
			sprint: '200',
			description: '',
			version: '', //3.03.5",
			changes: {
				changed: ['someDashboardAjustments'],
				added: ['addedBoxPlotToOutliersChart'],

				// changed: ["lorem ipsum"],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.13.5-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1679423498194, //new Date().getTime() no console do chrome
			sprint: '200',
			description: '',
			version: '', //3.03.4",
			changes: {
				changed: ['adjustOutliersCalculations'],
				added: [
					'availablePlantationInFarm',
					'addedLogLengthField',
					'addedOutliersExport',
					'addedForestStratificationEdition',
					'addedSampleImport',
					'addedSamplingIntensityRecommendation',
				],

				// changed: ["lorem ipsum"],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.13.4-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1678800943801, //new Date().getTime() no console do chrome
			sprint: '200',
			description: '',
			version: '', //3.03.3",
			changes: {
				changed: ['addedDashboardFarmMarkers'],
				added: [
					'dependencyCheckForRegistryDeletion',
					'addOpenCloseViewOSOption',
					'addOSProgressBar',
					'addAssortmentExport',
				],

				// changed: ["lorem ipsum"],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.13.3-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1678387183738, //new Date().getTime() no console do chrome
			sprint: '200',
			description: '',
			version: '', //3.03.2",
			changes: {
				changed: ['changedMinimumSamplesByPlantation', 'linkToLegacyInventoryReport'],
				added: ['allowShapeEdition', 'addTreeviaSupportLink', 'addSystemVersionToFooter'],

				// changed: ["lorem ipsum"],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.13.2-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1678126088864, //new Date().getTime() no console do chrome
			sprint: '200',
			description: '',
			version: '', //3.03.1",
			changes: {
				changed: ['enhancedUserExperienceFarmRegistry'],

				// changed: ["lorem ipsum"],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.13.1-alpha',
				},
			],
		},
		{
			type: 'minor',
			timestamp: 1677616275276,
			sprint: '198',
			description: '',
			version: '', //3.02.2",
			changes: {
				added: ['addedUsersRegistry', 'addedProfilesAccessRegistry'],

				// changed: ["lorem ipsum"],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.13.0-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1676400066028,
			sprint: '198',
			description: '',
			version: '', //3.02.1",
			changes: {
				added: ['adjustInventoryMeasurementVisualization', 'adjustLoggingMeasurementVisualization'],

				// changed: ["lorem ipsum"],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.12.7-alpha',
				},
			],
		},

		{
			type: 'patch',
			timestamp: 1674848188273,
			sprint: '197',
			description: '',
			version: '', //3.01.6",
			changes: {
				added: ['adjustLoggingProcess'],

				// changed: ["lorem ipsum"],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.12.6-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1674139841970,
			sprint: '196',
			description: '',
			version: '', //3.01.5",
			changes: {
				added: ['adjustInventoryProcess'],

				// changed: ["lorem ipsum"],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.12.5-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1673878196795,
			sprint: '196',
			description: '',
			version: '', //3.01.4",
			changes: {
				added: ['adjustForestStratum'],

				// changed: ["lorem ipsum"],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.12.4-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1673632378446,
			sprint: '196',
			description: '',
			version: '', //3.01.3",
			changes: {
				added: ['adjustCubbingCollect'],

				// changed: ["lorem ipsum"],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.12.3-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1673547452434,
			sprint: '196',
			description: '',
			version: '', //3.01.2",
			changes: {
				added: ['adjustSingleTreesInstallment', 'adjustSingleTreesCollect'],

				// changed: ["lorem ipsum"],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.12.2-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1673406000000,
			sprint: '196',
			description: '',
			version: '', //3.01.1",
			changes: {
				added: ['adjustInstallmentInventory', 'adjustContinuousInventory', 'adjustPreCutInventory'],

				// changed: [

				// ],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				{
					name: 'Smartforest Plantadas',
					version: '0.12.1-alpha',
				},
			],
		},
		{
			type: 'minor',
			timestamp: 1668709382951,
			sprint: '192',
			description: '',
			version: '', //2.11.3",
			changes: {
				added: ['addedAssortmentAndHistoricalAAI'],

				// changed: ["lorem ipsum"],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				// {
				//   name: "WalterBI API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Walter API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Mobile API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Registry API",
				//   version: "1.0.1",
				// },
				// {
				//   name: "OrdemServico API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Auth API",
				//   version: "1.0.0",
				// },
				{
					name: 'Smartforest Plantadas',
					version: '0.12.0-alpha',
				},
			],
		},
		{
			type: 'minor',
			timestamp: 1668017369220,
			sprint: '191',
			description: '',
			version: '', //2.11.2",
			changes: {
				added: ['addedServiceOrderReport'],

				// changed: ["lorem ipsum"],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				// {
				//   name: "WalterBI API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Walter API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Mobile API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Registry API",
				//   version: "1.0.1",
				// },
				// {
				//   name: "OrdemServico API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Auth API",
				//   version: "1.0.0",
				// },
				{
					name: 'Smartforest Plantadas',
					version: '0.11.0-alpha',
				},
			],
		},
		{
			type: 'minor',
			timestamp: 1667934452926,
			sprint: '191',
			description: '',
			version: '', //2.11.1",
			changes: {
				added: ['addedInventoryReport'],

				// changed: ["lorem ipsum"],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				// {
				//   name: "WalterBI API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Walter API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Mobile API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Registry API",
				//   version: "1.0.1",
				// },
				// {
				//   name: "OrdemServico API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Auth API",
				//   version: "1.0.0",
				// },
				{
					name: 'Smartforest Plantadas',
					version: '0.10.0-alpha',
				},
			],
		},
		{
			type: 'minor',
			timestamp: 1666271281808,
			sprint: '190',
			description: '',
			version: '', //2.10.1",
			changes: {
				added: ['addedInventoryMeasurementImport', 'addedCubbingMeasurementImport'],

				// changed: ["lorem ipsum"],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				// {
				//   name: "WalterBI API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Walter API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Mobile API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Registry API",
				//   version: "1.0.1",
				// },
				// {
				//   name: "OrdemServico API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Auth API",
				//   version: "1.0.0",
				// },
				{
					name: 'Smartforest Plantadas',
					version: '0.9.0-alpha',
				},
			],
		},
		{
			type: 'minor',
			timestamp: 1663097188000,
			sprint: '187',
			description: '',
			version: '', //2.09.1",
			changes: {
				added: [
					'addedSectionExport',
					'addedPlotExport',
					'addedSingleTreesExport',
					'addedTreeExport',
				],

				// changed: ["lorem ipsum"],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				// {
				//   name: "WalterBI API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Walter API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Mobile API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Registry API",
				//   version: "1.0.1",
				// },
				// {
				//   name: "OrdemServico API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Auth API",
				//   version: "1.0.0",
				// },
				{
					name: 'Smartforest Plantadas',
					version: '0.8.1-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1655753429448,
			sprint: '182',
			description: '',
			version: '', //2.06.4",
			changes: {
				added: ['addedChangeLogPage'],

				// changed: ["lorem ipsum"],
				// deprecated: ["lorem ipsum"],
				// removed: ["lorem ipsum"],
				// security: ["lorem ipsum"],
			},
			products: [
				// {
				//   name: "WalterBI API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Walter API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Mobile API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Registry API",
				//   version: "1.0.1",
				// },
				// {
				//   name: "OrdemServico API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Auth API",
				//   version: "1.0.0",
				// },
				{
					name: 'Smartforest Plantadas',
					version: '0.7.2-alpha',
				},
			],
		},
		{
			type: 'patch',
			timestamp: 1655732591557,
			sprint: '184',
			description: 'ajustes menores',
			version: '', //2.06.3",
			changes: {
				added: [
					"Adiciona botão de 'selecionar todos', 'limpar selecionados' na tela de Inventário Single Trees.",
				],
				fixed: [
					'Ajusta verificação de dados de ciclo, rotação que resultava em erro ao buscar mapas para planejamento de estrato florestal.',
				],
				//changed: ["lorem ipsum"],
				//deprecated: ["lorem ipsum"],
				//removed: ["lorem ipsum"],
				//security: ["lorem ipsum"],
			},
			products: [
				// {
				//   name: "WalterBI API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Walter API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Mobile API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Registry API",
				//   version: "1.0.1",
				// },
				// {
				//   name: "OrdemServico API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Auth API",
				//   version: "1.0.0",
				// },
				{
					name: 'Smartforest Plantadas',
					version: '1.0.0-alpha',
				},
			],
		},
		{
			type: 'minor',
			timestamp: 1655732591557,
			sprint: '184',
			description: 'Hoje trouxemos mudanças para complementar a experiência nas telas de cadastro',
			version: '', //2.06.2",
			changes: {
				added: ['Adição de menu para ações na tabela de registros'],
			},
			products: [
				// {
				//   name: "WalterBI API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Walter API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Mobile API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Registry API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "OrdemServico API",
				//   version: "1.0.0",
				// },
				// {
				//   name: "Auth API",
				//   version: "1.0.0",
				// },
				{
					name: 'Smartforest Plantadas',
					version: '1.0.0-alpha',
				},
			],
		},
	],
}

const mutations = {}

const actions = {}

const getters = {
	getReleases: (state) => state.releases,
	getAboutToRelease: (state) => state.aboutToRelease,
	latestVersion: (state) =>
		JSON.parse(JSON.stringify(state.releases))
			.sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1))[0]
			.products.find((p) => p.name === 'Smartforest Plantadas').version,
}

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
}
